import { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions, IconButton, Grid, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import { AccrualDate } from 'repository/timekeeper/@types/AccrualDate';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

enum TipoDateEnum {
    FECHA_ESTABLECIMIENTO = 'FECHA_ESTABLECIMIENTO',
    FECHA_ESPECIFICA_PERSONA = 'FECHA_ESPECIFICA_PERSONA',
}

const timekeeperService = new TimekeeperService();

interface ReadProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rowToEdit: AccrualDate;
    cleanSelected: () => void;
}

const normalizeTypeDate = (typeDate: string): TipoDateEnum => {
    if (typeDate === TipoDateEnum.FECHA_ESTABLECIMIENTO || typeDate === 'Fecha de establecimiento') {
        return TipoDateEnum.FECHA_ESTABLECIMIENTO;
    }

    if (typeDate === TipoDateEnum.FECHA_ESPECIFICA_PERSONA || typeDate === 'Fecha específica de persona') {
        return TipoDateEnum.FECHA_ESPECIFICA_PERSONA;
    }

    return TipoDateEnum.FECHA_ESTABLECIMIENTO;
};

const ReadAccrualDate: FC<ReadProps> = ({ open, setOpen, rowToEdit, cleanSelected }) => {
    const { t } = useTranslation();
    const [availablePersonDates, setAvailablePersonDates] = useState<AccrualDate[]>([]);

    const [data, setData] = useState<AccrualDate[]>([
        {
            accrual_date_id: 0,
            id: 0,
            name: '',
            date: null,
            type_date: TipoDateEnum.FECHA_ESTABLECIMIENTO,
            date_person_id: null,
        },
    ]);

    useEffect(() => {
        const loadData = async () => {
            const response = await timekeeperService.accrual_date.getAll({
                type_date__eq: TipoDateEnum.FECHA_ESPECIFICA_PERSONA,
            });
            setAvailablePersonDates(response.length > 0 ? response : []);
        };

        if (open && rowToEdit) {
            loadData();
            setData([
                {
                    ...rowToEdit,
                    type_date: normalizeTypeDate(String(rowToEdit.type_date)),
                },
            ]);
        }
    }, [open, rowToEdit]);

    const closeForm = () => {
        setOpen(false);
        cleanSelected();
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                closeForm();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_dates.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => closeForm()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label={t('accruals.accrual_dates.dialog.inputs.name')} autoComplete="off" value={data[0].name} disabled={true} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth disabled={true}>
                            <InputLabel>{t('accruals.accrual_dates.dialog.inputs.type_date')}</InputLabel>
                            <Select value={data[0].type_date} label={t('accruals.accrual_dates.dialog.inputs.type_date')}>
                                <MenuItem value={TipoDateEnum.FECHA_ESTABLECIMIENTO}>{t('accruals.accrual_dates.dialog.sections.establishment_dates')}</MenuItem>
                                <MenuItem value={TipoDateEnum.FECHA_ESPECIFICA_PERSONA}>{t('accruals.accrual_dates.dialog.sections.person_specific_dates')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {data[0].type_date === TipoDateEnum.FECHA_ESTABLECIMIENTO && (
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <DatePicker
                                label={t('accruals.accrual_dates.dialog.inputs.date')}
                                value={data[0].date ? dayjs(data[0].date) : null}
                                format="L"
                                disabled
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Grid>
                    )}

                    {data[0].type_date === TipoDateEnum.FECHA_ESPECIFICA_PERSONA && (
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <FormControl fullWidth disabled={true}>
                                <InputLabel>{t('accruals.accrual_dates.dialog.inputs.date')}</InputLabel>
                                <Select value={data[0].date_person_id ?? ''} fullWidth label={t('accruals.accrual_dates.dialog.inputs.date')}>
                                    <MenuItem value={0}>{t('accruals.accrual_dates.dialog.inputs.types_date_selectable.hire_date')}</MenuItem>
                                    {availablePersonDates.map((dateOption) => (
                                        <MenuItem key={dateOption.accrual_date_id} value={dateOption.accrual_date_id}>
                                            {dateOption.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => closeForm()}>
                    {t('general.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { ReadAccrualDate };
